/**
 * core-fallback.js
 * ----------------
 * Este tema (Bitcoworld PRO) carga su JavaScript principal desde un servidor
 * externo del proveedor: https://bitcoworld.netlify.app/js/v2/main.js
 * Ese archivo actualmente responde VACIO, por lo que se pierden funciones
 * esenciales (debounce, el elemento <variant-selects>, pauseAllMedia...) y
 * deja de funcionar la seleccion de imagenes en la pagina de producto.
 *
 * Este archivo define esas piezas SOLO si aun no existen, de modo que es
 * seguro incluso si el script del proveedor vuelve a funcionar algun dia.
 */

(function () {
  'use strict';

  /* ---- Utilidades globales ---------------------------------------------- */

  if (typeof window.debounce !== 'function') {
    window.debounce = function (fn, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    };
  }

  if (typeof window.pauseAllMedia !== 'function') {
    window.pauseAllMedia = function () {
      document.querySelectorAll('.js-youtube').forEach((video) => {
        try {
          video.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
        } catch (e) {
          /* iframe sin acceso: ignorar */
        }
      });
      document.querySelectorAll('.js-vimeo').forEach((video) => {
        try {
          video.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (e) {
          /* iframe sin acceso: ignorar */
        }
      });
      document.querySelectorAll('video').forEach((video) => video.pause());
      document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    };
  }

  /* ---- <variant-selects>: cambio de variante e imagen -------------------- */

  if (!customElements.get('variant-selects')) {
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          // Primero lo critico (input del carrito y URL); la galeria despues,
          // para que un error visual nunca deje el formulario desactualizado.
          this.updateVariantInput();
          this.updateURL();
          try {
            this.updateMedia();
          } catch (error) {
            console.error('[core-fallback] galeria:', error);
          }
          this.renderProductInfo();
        }

        document.dispatchEvent(
          new CustomEvent('variant:change', {
            detail: {
              sectionId: this.dataset.section,
              variant: this.currentVariant || null,
            },
          })
        );
      }

      updateOptions() {
        this.options = Array.from(
          this.querySelectorAll('.product-form__input')
        ).map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          const checked = group.querySelector('input[type="radio"]:checked');
          return checked ? checked.value : null;
        });
      }

      getVariantData() {
        if (!this.variantData) {
          const json = this.querySelector('script[type="application/json"]');
          this.variantData = json ? JSON.parse(json.textContent) : [];
        }
        return this.variantData;
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) =>
          variant.options.every((option, index) => option === this.options[index])
        );
      }

      updateMedia() {
        if (!this.currentVariant) return;
        const sectionId = this.dataset.section;
        const hasFiltering = this.dataset.hasFiltering === 'true';
        const galleries = document.querySelectorAll(
          'media-gallery[id^="MediaGallery-' + sectionId + '"]'
        );

        galleries.forEach((gallery) => {
          if (typeof gallery.setActiveMedia !== 'function') return;

          if (this.currentVariant.featured_media) {
            gallery.setActiveMedia(
              sectionId + '-' + this.currentVariant.featured_media.id,
              true,
              hasFiltering,
              this.currentVariant
            );
            return;
          }

          if (hasFiltering) {
            // La variante no tiene imagen destacada: aplicar solo el filtrado
            // por texto alternativo y activar la primera imagen visible.
            const filteringOption = gallery.dataset.filteringOption;
            const optionValue = this.currentVariant[filteringOption];
            gallery
              .querySelectorAll(
                '.product__media-item, .thumbnail-list__item, .slider-counter__link'
              )
              .forEach((slide) => {
                if (
                  slide.dataset.alt === optionValue ||
                  slide.dataset.alt === 'always_display'
                ) {
                  slide.classList.remove('hidden');
                } else {
                  slide.classList.add('hidden');
                }
              });

            const firstVisible = gallery.querySelector(
              '.product__media-item:not(.hidden)[data-media-id]'
            );
            if (firstVisible) {
              gallery.setActiveMedia(firstVisible.dataset.mediaId, false);
            }
          }
        });
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          {},
          '',
          this.dataset.url + '?variant=' + this.currentVariant.id
        );
      }

      updateVariantInput() {
        const sectionId = this.dataset.section;
        const productForms = document.querySelectorAll(
          '#product-form-' +
            sectionId +
            ', #product-form-installment-' +
            sectionId
        );
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      renderProductInfo() {
        const requestedId = this.currentVariant.id;
        const sectionId = this.dataset.originalSection || this.dataset.section;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        fetch(
          this.dataset.url + '?variant=' + requestedId + '&section_id=' + sectionId,
          { signal: this.abortController.signal }
        )
          .then((response) => response.text())
          .then((responseText) => {
            if (!this.currentVariant || this.currentVariant.id !== requestedId)
              return;
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            // Precio
            const priceDestination = document.getElementById(
              'price-' + this.dataset.section
            );
            const priceSource = html.getElementById('price-' + sectionId);
            if (priceDestination && priceSource) {
              priceDestination.innerHTML = priceSource.innerHTML;
              priceDestination.classList.remove('visibility-hidden');
            }

            // Boton "Agregar al carrito": texto y estado (agotado/disponible)
            const buttonDestination = document.getElementById(
              'ProductSubmitButton-' + this.dataset.section
            );
            const buttonSource = html.getElementById(
              'ProductSubmitButton-' + sectionId
            );
            if (buttonDestination && buttonSource) {
              const labelDestination =
                buttonDestination.querySelector('.main-atc__label');
              const labelSource = buttonSource.querySelector('.main-atc__label');
              if (labelDestination && labelSource) {
                labelDestination.innerHTML = labelSource.innerHTML;
              }
              if (buttonSource.hasAttribute('disabled')) {
                buttonDestination.setAttribute('disabled', 'disabled');
              } else {
                buttonDestination.removeAttribute('disabled');
              }
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('[core-fallback]', error);
            }
          });
      }

      setUnavailable() {
        const button = document.getElementById(
          'ProductSubmitButton-' + this.dataset.section
        );
        if (!button) return;
        button.setAttribute('disabled', 'disabled');
        const text = button.querySelector('.main-atc__label__text');
        if (text && window.variantStrings && window.variantStrings.unavailable) {
          text.textContent = window.variantStrings.unavailable;
        }
      }
    }

    customElements.define('variant-selects', VariantSelects);

    if (!customElements.get('variant-radios')) {
      customElements.define('variant-radios', class extends VariantSelects {});
    }
  }
})();
